import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ClockIcon, AlertTriangleIcon, RocketIcon } from 'lucide-react'
import { MetricChart } from './MetricChart'
import type { Summary, RequestsData } from './types'

interface SummaryStatsRowProps {
  summary: Summary
  requestsData?: RequestsData | null
}

function SparklineBar({ daily }: { daily: number[] }) {
  const max = Math.max(...daily, 1)
  const days = daily.map((value, i) => {
    const date = new Date()
    date.setDate(date.getDate() - (daily.length - 1 - i))
    return { value, label: date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) }
  })

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-end gap-0.5 h-8 mt-2">
        {days.map((day, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div
                className="flex-1 bg-primary/60 hover:bg-primary rounded-sm min-h-[2px] transition-colors cursor-default"
                style={{ height: `${(day.value / max) * 100}%` }}
              />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p className="font-medium">{day.label}</p>
              <p className="text-muted-foreground">{day.value} deploys</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return n.toString()
}

export function SummaryStatsRow({ summary, requestsData }: SummaryStatsRowProps) {
  const { t } = useTranslation('projects')

  const requestsTotal = requestsData?.total ?? 0
  const requestsPrev = requestsData?.previous_total ?? 0
  const requestsPctChange = requestsPrev === 0
    ? (requestsTotal > 0 ? 100 : 0)
    : ((requestsTotal - requestsPrev) / requestsPrev) * 100
  const requestsIsPositive = requestsPctChange >= 0

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Requests (24h) */}
      <Card className="shadow-none">
        <CardHeader className="pb-1">
          <div className="flex items-center justify-between">
            <CardDescription>{t('production.summary.requests24h')}</CardDescription>
            <ClockIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex items-baseline gap-2">
            <CardTitle className="text-3xl">{formatNumber(requestsTotal)}</CardTitle>
            {requestsPrev > 0 && (
              <span className={`text-xs font-medium ${requestsIsPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {requestsIsPositive ? '+' : ''}{requestsPctChange.toFixed(0)}% {t('production.summary.vsYesterday')}
              </span>
            )}
          </div>
        </CardHeader>
        {requestsData?.hourly && requestsData.hourly.length > 0 && (
          <CardContent className="pt-0">
            <MetricChart datapoints={requestsData.hourly} color="hsl(210, 70%, 55%)" height={32} label={t('production.apps.requests')} />
          </CardContent>
        )}
      </Card>

      {/* Exceptions */}
      <Card className="shadow-none">
        <CardHeader className="pb-1">
          <div className="flex items-center justify-between">
            <CardDescription>{t('production.summary.exceptions')}</CardDescription>
            <AlertTriangleIcon className={`h-4 w-4 ${summary.total_exceptions > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
          </div>
          <CardTitle className={`text-3xl ${summary.total_exceptions > 0 ? 'text-orange-600 dark:text-orange-400' : ''}`}>
            {summary.total_exceptions}
          </CardTitle>
          {summary.apps_with_exceptions > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('production.summary.acrossApps', { count: summary.apps_with_exceptions })}
            </p>
          )}
        </CardHeader>
      </Card>

      {/* Deploys (7d) */}
      <Card className="shadow-none">
        <CardHeader className="pb-1">
          <div className="flex items-center justify-between">
            <CardDescription>{t('production.summary.deploys7d')}</CardDescription>
            <RocketIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex items-baseline gap-2">
            <CardTitle className="text-3xl">{summary.deploy_stats.total_7d}</CardTitle>
            {summary.deploy_stats.today > 0 && (
              <span className="text-xs font-medium text-green-600 dark:text-green-400">
                {t('production.summary.today', { count: summary.deploy_stats.today })}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <SparklineBar daily={summary.deploy_stats.daily} />
        </CardContent>
      </Card>

    </div>
  )
}
